import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, CheckCircle2, AlarmClock, SkipForward, ExternalLink, Users, User, Target, PartyPopper } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { categoryColor, categoryLabel, stripMentionIds, formatDateTime } from "../lib/constants";
import { snoozeOptions, shortWhen, shortTitle, dueShort } from "../lib/taskActions";

const LEAVE_MS = 320;
const CONFETTI = ["#10b981", "#3b82f6", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899"];

function Confetti() {
  const bits = useMemo(() => Array.from({ length: 36 }, (_, i) => ({
    left: 50 + (Math.random() - 0.5) * 20,
    dx: (Math.random() - 0.5) * 520,
    dy: -(160 + Math.random() * 260),
    rot: Math.random() * 720 - 360,
    color: CONFETTI[i % CONFETTI.length],
    delay: Math.random() * 120,
    w: 6 + Math.random() * 6,
  })), []);
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {bits.map((b, i) => (
        <span key={i} className="absolute top-1/2 sw-confetti rounded-[2px]"
          style={{ left: `${b.left}%`, width: b.w, height: b.w * 0.45, background: b.color, animationDelay: `${b.delay}ms`,
            "--dx": `${b.dx}px`, "--dy": `${b.dy}px`, "--rot": `${b.rot}deg` }} />
      ))}
    </div>
  );
}

// Full-screen, one-task-at-a-time triage of overdue + due-today work.
// Opens on the "brainydo:focus-mode" window event.
export default function FocusMode() {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [queue, setQueue] = useState([]);
  const [total, setTotal] = useState(0);
  const [tally, setTally] = useState({ done: 0, snoozed: 0 });
  const [leaving, setLeaving] = useState(null); // "done" | "snoozed" | "skip"
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const onOpen = async () => {
      setOpen(true); setLoading(true); setTally({ done: 0, snoozed: 0 }); setSnoozeOpen(false);
      try {
        const r = await api.get("/tasks", { params: { in_review: false } });
        const end = new Date(); end.setHours(24, 0, 0, 0);
        const due = (r.data.tasks || [])
          .filter((t) => (t.status === "pending" || t.status === "in_progress") && t.deadline && new Date(t.deadline) < end)
          .sort((a, b) => new Date(a.deadline) - new Date(b.deadline));
        setQueue(due); setTotal(due.length);
      } catch { toast.error("Couldn't load tasks"); setQueue([]); }
      finally { setLoading(false); }
    };
    window.addEventListener("brainydo:focus-mode", onOpen);
    return () => window.removeEventListener("brainydo:focus-mode", onOpen);
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    if (tally.done || tally.snoozed) window.dispatchEvent(new CustomEvent("brainydo:tasks-changed"));
  }, [tally]);

  const cur = queue[0];

  const advance = (kind, requeue = false) => {
    setLeaving(kind);
    setSnoozeOpen(false);
    setTimeout(() => {
      setQueue((q) => (requeue ? [...q.slice(1), q[0]] : q.slice(1)));
      setLeaving(null);
    }, LEAVE_MS);
  };

  const restore = (t, patch, key) => async () => {
    try {
      await api.patch(`/tasks/${t.id}`, patch);
      setQueue((q) => [t, ...q]);
      setTally((c) => ({ ...c, [key]: Math.max(0, c[key] - 1) }));
    } catch { toast.error("Undo failed"); }
  };

  const done = async () => {
    if (!cur || leaving) return;
    const t = cur;
    advance("done");
    try {
      await api.patch(`/tasks/${t.id}`, { status: "completed" });
      setTally((c) => ({ ...c, done: c.done + 1 }));
      toast.success(`Done: ${shortTitle(t)}`, { action: { label: "Undo", onClick: restore(t, { status: t.status }, "done") }, duration: 5000 });
    } catch { toast.error("Save failed"); setTimeout(() => setQueue((q) => [t, ...q]), LEAVE_MS + 20); }
  };

  const snooze = async (date) => {
    if (!cur || leaving) return;
    const t = cur;
    advance("snoozed");
    try {
      await api.patch(`/tasks/${t.id}`, { deadline: date.toISOString() });
      setTally((c) => ({ ...c, snoozed: c.snoozed + 1 }));
      toast(`Moved to ${shortWhen(date)}`, { action: { label: "Undo", onClick: restore(t, { deadline: t.deadline }, "snoozed") }, duration: 5000 });
    } catch { toast.error("Save failed"); setTimeout(() => setQueue((q) => [t, ...q]), LEAVE_MS + 20); }
  };

  const skip = () => { if (cur && !leaving && queue.length > 1) advance("skip", true); };

  useEffect(() => {
    if (!open) return;
    const opts = snoozeOptions();
    const onKey = (e) => {
      if (e.target.closest?.("input, textarea, [contenteditable]")) return;
      const k = e.key.toLowerCase();
      if (k === "escape") { if (snoozeOpen) setSnoozeOpen(false); else close(); return; }
      if (!cur) return;
      if (snoozeOpen && /^[1-9]$/.test(k) && opts[+k - 1]) { snooze(opts[+k - 1].date); return; }
      if (k === "d" || k === "enter") done();
      else if (k === "s") setSnoozeOpen((v) => !v);
      else if (k === "arrowright" || k === "k") skip();
      else if (k === "o") { close(); nav(`/app/tasks?focus=${cur.id}`); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }); // re-bind each render so handlers see current state

  if (!open) return null;

  const handled = total - queue.length;
  const pct = total ? Math.round((handled * 100) / total) : 100;
  const leaveClass = leaving === "done" ? "sw-card-done" : leaving === "snoozed" ? "sw-card-snooze" : leaving === "skip" ? "sw-card-skip" : "";

  return (
    <div className="fixed inset-0 z-[65] bg-slate-950/85 backdrop-blur-sm flex flex-col sw-fade-in" data-testid="focus-mode">
      {/* Top bar: progress */}
      <div className="flex items-center gap-4 px-5 md:px-8 py-4 text-white">
        <div className="flex items-center gap-2 text-[13px] font-semibold"><Target size={16} className="text-emerald-400" /> Focus mode</div>
        <div className="flex-1 max-w-md h-1.5 bg-white/10 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-emerald-400 to-sky-400 rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
        </div>
        <div className="text-[12.5px] text-white/70 tabular-nums">{queue.length ? `${handled + 1} of ${total}` : `${total} of ${total}`}</div>
        <div className="hidden md:flex items-center gap-3 text-[12px] text-white/60">
          <span className="text-emerald-300 font-semibold">✓ {tally.done}</span>
          <span className="text-amber-300 font-semibold">⏰ {tally.snoozed}</span>
        </div>
        <button onClick={close} className="ml-auto md:ml-0 h-9 w-9 grid place-items-center rounded-lg hover:bg-white/10" aria-label="Close focus mode"><X size={18} /></button>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 pb-8 relative">
        {loading ? (
          <div className="text-white/60 text-[14px]">Lining up your tasks…</div>
        ) : !cur ? (
          <div className="relative text-center text-white sw-rise-bounce max-w-md">
            <Confetti />
            <div className="w-20 h-20 mx-auto rounded-full bg-emerald-500/20 grid place-items-center"><PartyPopper size={40} className="text-emerald-300" /></div>
            <div className="text-[28px] font-extrabold mt-4">{total ? "All clear!" : "Nothing due right now"}</div>
            <p className="text-white/70 mt-1 text-[14px]">
              {total ? <>You closed <b className="text-emerald-300">{tally.done}</b> and snoozed <b className="text-amber-300">{tally.snoozed}</b>. Go grab a chai ☕</> : "No overdue or due-today tasks. Enjoy the calm."}
            </p>
            <button onClick={close} className="mt-6 h-11 px-6 rounded-xl bg-white text-slate-900 font-semibold text-[14px] hover:bg-slate-100 active:scale-95 transition">Back to work</button>
          </div>
        ) : (
          <div className="relative w-full max-w-xl">
            {/* Peeking cards behind */}
            {queue[2] && <div className="absolute inset-x-8 -bottom-5 h-full bg-white/40 rounded-3xl" />}
            {queue[1] && <div className="absolute inset-x-4 -bottom-2.5 h-full bg-white/70 rounded-3xl transition-all" />}

            <div key={cur.id} className={"relative bg-white rounded-3xl shadow-2xl p-6 md:p-7 " + (leaveClass || "sw-pop")} data-testid="focus-card">
              <div className="flex items-center gap-2 flex-wrap">
                {(() => { const d = dueShort(cur.deadline); return (
                  <span className={"text-[12px] font-bold px-2 py-1 rounded-lg " + (d.tone === "red" ? "bg-red-50 text-red-700" : d.tone === "amber" ? "bg-amber-50 text-amber-800" : "bg-slate-100 text-slate-700")}>{d.text}</span>
                ); })()}
                <span className="text-[10px] tracking-[0.14em] uppercase font-bold px-1.5 py-1 border rounded" style={{ borderColor: categoryColor(cur.category), color: categoryColor(cur.category) }}>{categoryLabel(cur.category)}</span>
                {(cur.priority === "urgent" || cur.priority === "high") && (
                  <span className={"text-[10px] font-bold uppercase tracking-wider px-1.5 py-1 rounded border " + (cur.priority === "urgent" ? "text-red-700 bg-red-50 border-red-200" : "text-amber-700 bg-amber-50 border-amber-200")}>{cur.priority}</span>
                )}
                {cur.task_no && <span className="ml-auto font-mono text-[12px] text-slate-400">#{cur.task_no}</span>}
              </div>
              <h2 className="text-[22px] md:text-[24px] font-extrabold text-slate-900 leading-tight mt-3">{cur.title}</h2>
              {cur.summary && <p className="text-[14px] text-slate-600 mt-2">{cur.summary}</p>}
              {cur.original_text && (
                <div className="mt-4 p-3 rounded-xl bg-slate-50 border border-slate-100 text-[13px] text-slate-700 whitespace-pre-wrap line-clamp-5">
                  {stripMentionIds(cur.original_text)}
                </div>
              )}
              <div className="mt-3 flex items-center gap-3 text-[12px] text-slate-500 flex-wrap">
                {(cur.sender_name || cur.chat_name) && (
                  <span className="inline-flex items-center gap-1">{cur.chat_type === "group" ? <Users size={12} /> : <User size={12} />}
                    {cur.chat_type === "group" ? `${stripMentionIds(cur.chat_name || "")} › ${cur.sender_name || ""}` : (cur.sender_name || cur.chat_name)}</span>
                )}
                {cur.deadline && <span>Due {formatDateTime(cur.deadline)}</span>}
                {cur.assignee && <span>· {cur.assignee}</span>}
              </div>

              {/* Actions */}
              <div className="mt-6 grid grid-cols-3 gap-2">
                <button onClick={done} className="h-12 rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-[14px] inline-flex items-center justify-center gap-2 active:scale-95 transition" data-testid="focus-done">
                  <CheckCircle2 size={18} /> Done <kbd className="hidden md:inline text-[10px] font-mono bg-white/20 rounded px-1">D</kbd>
                </button>
                <div className="relative">
                  <button onClick={() => setSnoozeOpen((v) => !v)} className={"w-full h-12 rounded-2xl border-2 font-semibold text-[14px] inline-flex items-center justify-center gap-2 active:scale-95 transition " + (snoozeOpen ? "border-amber-400 bg-amber-50 text-amber-800" : "border-slate-200 text-slate-700 hover:border-amber-400")} data-testid="focus-snooze">
                    <AlarmClock size={18} /> Snooze <kbd className="hidden md:inline text-[10px] font-mono bg-slate-100 rounded px-1">S</kbd>
                  </button>
                  {snoozeOpen && (
                    <div className="absolute bottom-14 left-1/2 -translate-x-1/2 w-64 bg-white border border-slate-200 rounded-2xl shadow-2xl py-2 sw-pop z-10">
                      {snoozeOptions().map((o, i) => (
                        <button key={o.key} onClick={() => snooze(o.date)} className="w-full flex items-center gap-2 px-3 py-2 text-left text-[13px] hover:bg-amber-50">
                          <kbd className="text-[10px] font-mono bg-slate-100 border border-slate-200 rounded w-5 text-center">{i + 1}</kbd>
                          <span className="flex-1 text-slate-800">{o.label}</span>
                          <span className="text-[11px] text-slate-400">{shortWhen(o.date)}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button onClick={skip} disabled={queue.length < 2} className="h-12 rounded-2xl border-2 border-slate-200 text-slate-600 hover:border-slate-400 font-semibold text-[14px] inline-flex items-center justify-center gap-2 active:scale-95 transition disabled:opacity-40" data-testid="focus-skip">
                  <SkipForward size={18} /> Skip <kbd className="hidden md:inline text-[10px] font-mono bg-slate-100 rounded px-1">→</kbd>
                </button>
              </div>
              <button onClick={() => { close(); nav(`/app/tasks?focus=${cur.id}`); }} className="mt-3 w-full text-[12px] text-slate-400 hover:text-blue-600 inline-flex items-center justify-center gap-1">
                <ExternalLink size={12} /> Open full details, reply or reassign <kbd className="font-mono text-[10px]">O</kbd>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
